import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import './Services.css';

// Optimized WebP assets
import automotive400 from '../assets/images/services/optimized/automotive-400.webp';
import automotive800 from '../assets/images/services/optimized/automotive-800.webp';
import architectural400 from '../assets/images/services/optimized/architectural-400.webp';
import architectural800 from '../assets/images/services/optimized/architectural-800.webp';
import industrial400 from '../assets/images/services/optimized/industrial-400.webp';
import industrial800 from '../assets/images/services/optimized/industrial-800.webp';
import furniture400 from '../assets/images/services/optimized/furniture-400.webp';
import furniture800 from '../assets/images/services/optimized/furniture-800.webp';
import custom400 from '../assets/images/services/optimized/custom-400.webp';
import custom800 from '../assets/images/services/optimized/custom-800.webp';

const Services = () => {
  const [flippedCards, setFlippedCards] = useState(new Set());
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const handleCardClick = (index) => {
    // Only handle clicks on touch devices (mobile/tablet)
    // Desktop will use hover instead
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      setFlippedCards(prev => {
        const newSet = new Set(prev);
        if (newSet.has(index)) {
          newSet.delete(index);
        } else {
          newSet.add(index);
        }
        return newSet;
      });
    }
  };

  const services = [
    {
      icon: '/icons/car.svg',
      title: 'Automotive Powder Coating',
      description: 'We specialize in coating wheels, frames, suspension parts, and engine components with heat-resistant, chip-resistant finishes. Improve both the aesthetics and durability of your vehicle with custom colors and finishes.',
      image400: automotive400,
      image800: automotive800
    },
    {
      icon: '/icons/building.svg',
      title: 'Architectural Powder Coating',
      description: 'Ideal for fences, gates, handrails, metal doors, and window frames, our architectural coatings are UV- and weather-resistant, ensuring long-term durability and color retention.',
      image400: architectural400,
      image800: architectural800
    },
    {
      icon: '/icons/industry.svg',
      title: 'Industrial Powder Coating',
      description: 'Protect your equipment, tools, and fabricated metal parts with coatings that stand up to chemicals, corrosion, and wear. We offer industrial-grade finishes for manufacturing, construction, and heavy machinery applications.',
      image400: industrial400,
      image800: industrial800
    },
    {
      icon: '/icons/couch.svg',
      title: 'Furniture & Decorative Metal Coating',
      description: 'Transform indoor and outdoor metal furniture, lighting fixtures, and artistic elements with our wide range of colors, textures, and specialty effects.',
      image400: furniture400,
      image800: furniture800
    },
    {
      icon: '/icons/palette.svg',
      title: 'Custom Powder Coating Projects',
      description: 'Bring your ideas to life. Whether it\'s a one-of-a-kind project or a unique metal piece, we offer fully customized coating solutions to match your vision and requirements.',
      image400: custom400,
      image800: custom800
    }
  ];

  const specialtyServices = [
    {
      icon: '/icons/shield-alt.svg',
      title: 'Hyper Durable Powder Coatings',
      description: 'Formulated for maximum resistance to UV exposure, chemicals, and physical wear. These coatings are ideal for harsh environments and long-term outdoor use where finish integrity is critical.'
    },
    {
      icon: '/icons/star.svg',
      title: 'Super Durable Powder Coating',
      description: 'Designed for projects needing enhanced color stability, weather protection, and extended coating life. Commonly used in architectural, marine, and high-traffic commercial settings.'
    }
  ];

  const additionalServices = [
    'Surface Preparation',
    'Color Matching & Finish Consultation',
    'Pickup & Delivery Options Available (Depends on Area)',
    'Quick Turnaround for Urgent Projects'
  ];

  // Removed unused scrollToSection function to fix ESLint errors

  return (
    <section id="services" className="services">
      <div className="container">
        <div className="section-header">
          <h2>Powder Coating Services</h2>
          <p>We provide comprehensive powder coating solutions designed for durability, performance, and visual appeal—tailored to the needs of residential, commercial, and industrial clients.</p>
        </div>
        
        <div className="services-grid" ref={ref}>
          {services.map((service, index) => (
            <div 
              key={index}
              className={`flip-card ${inView ? 'fade-in-up' : ''} ${flippedCards.has(index) ? 'flipped' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => handleCardClick(index)}
              data-flipped={flippedCards.has(index)}
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="service-image">
                    <picture>
                      <source type="image/webp" srcSet={`${service.image400} 400w, ${service.image800} 800w`} sizes="(max-width: 768px) 45vw, 400px" />
                      <img 
                        src={service.image800}
                        alt={service.title}
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="300"
                      />
                    </picture>
                  </div>
                  <div className="service-title-overlay">
                    <h3>{service.title}</h3>
                  </div>
                </div>
                <div className="flip-card-back">
                  <div className="service-icon-watermark">
                    <img src={service.icon} alt={service.title} className="service-watermark-icon" />
                  </div>
                  <div className="service-content">
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="specialty-services">
          <h3>Specialty Coatings</h3>
          <p>In addition to our standard finishes, we offer a selection of premium and performance-based coatings designed for extreme durability and specialized applications:</p>
          <div className="specialty-grid">
            {specialtyServices.map((service, index) => (
              <div 
                key={index}
                className={`flip-card ${flippedCards.has(index + services.length) ? 'flipped' : ''}`}
                onClick={() => handleCardClick(index + services.length)}
                data-flipped={flippedCards.has(index + services.length)}
              >
                <div className="flip-card-inner">
                  <div className="flip-card-front">
                    <div className="service-icon">
                      <img src={service.icon} alt={service.title} className="service-front-icon" />
                    </div>
                    <h3>{service.title}</h3>
                  </div>
                  <div className="flip-card-back">
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="services-cta">
          <div className="cta-content">
            <h3>Additional Services</h3>
            <div className="additional-services">
              {additionalServices.map((service, index) => (
                <div key={index} className="additional-service">
                  <img src="/icons/check.svg" alt="Check" className="check-icon" />
                  <span>{service}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Services; 